import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Search, X, ChevronDown } from 'lucide-react'
import { useMedifarmaOpciones } from '@/modules/medifarma/hooks/useMedifarma'
import { getEstadoBadgeClass } from '../utils/estados'

interface RegistrosFiltrosProps {
  filters: {
    search?: string
    ciudades?: string[]
    estados?: string[]
  }
  onFiltersChange: (filters: any) => void
  onClearFilters: () => void
  stats?: {
    por_ciudad?: Record<string, number>
    por_estado?: Record<string, number>
  }
}

export function RegistrosFiltros({
  filters,
  onFiltersChange,
  onClearFilters,
  stats,
}: RegistrosFiltrosProps) {
  const { data: opciones } = useMedifarmaOpciones()
  const selectedCiudades = filters.ciudades || []
  const selectedEstados = filters.estados || []
  const hasActiveFilters = !!filters.search || selectedCiudades.length > 0 || selectedEstados.length > 0

  const handleCiudadToggle = (ciudad: string) => {
    const newCiudades = selectedCiudades.includes(ciudad)
      ? selectedCiudades.filter((c) => c !== ciudad)
      : [...selectedCiudades, ciudad]
    onFiltersChange({
      ...filters,
      ciudades: newCiudades.length > 0 ? newCiudades : undefined,
      page: 1,
    })
  }

  const handleEstadoToggle = (estado: string) => {
    const newEstados = selectedEstados.includes(estado)
      ? selectedEstados.filter((e) => e !== estado)
      : [...selectedEstados, estado]
    onFiltersChange({
      ...filters,
      estados: newEstados.length > 0 ? newEstados : undefined,
      page: 1,
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        {/* Provincia - Multiselect */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full md:w-auto md:min-w-[200px] justify-between">
              {selectedCiudades.length > 0
                ? `${selectedCiudades.length} provincia${selectedCiudades.length > 1 ? 's' : ''}`
                : 'Todas las provincias'}
              <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[250px] p-2 max-h-[300px] overflow-y-auto">
            <div className="space-y-2">
              {opciones?.ciudades?.map((ciudad) => (
                <label
                  key={ciudad.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer"
                >
                  <Checkbox
                    checked={selectedCiudades.includes(ciudad.value)}
                    onCheckedChange={() => handleCiudadToggle(ciudad.value)}
                  />
                  <span className="text-sm flex-1">{ciudad.label}</span>
                  <span className="text-xs bg-teal-500/10 text-teal-700 dark:text-teal-400 px-1.5 py-0.5 rounded">
                    {stats?.por_ciudad?.[ciudad.value] || 0}
                  </span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Estado - Multiselect */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full md:w-auto md:min-w-[180px] justify-between">
              {selectedEstados.length > 0 ? (
                <div className="flex items-center gap-1">
                  {selectedEstados.map((estado) => (
                    <Badge key={estado} className={`${getEstadoBadgeClass(estado)} text-xs`}>
                      {estado}
                    </Badge>
                  ))}
                </div>
              ) : (
                'Todos los estados'
              )}
              <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-2">
            <div className="space-y-2">
              {opciones?.estados?.map((estado) => (
                <label
                  key={estado.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer"
                >
                  <Checkbox
                    checked={selectedEstados.includes(estado.value)}
                    onCheckedChange={() => handleEstadoToggle(estado.value)}
                  />
                  <Badge className={`${getEstadoBadgeClass(estado.value)} text-xs`}>
                    {estado.label}
                  </Badge>
                  <span className="text-xs bg-teal-500/10 text-teal-700 dark:text-teal-400 px-1.5 py-0.5 rounded ml-auto">
                    {stats?.por_estado?.[estado.value] || 0}
                  </span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Botón limpiar filtros */}
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters} className="shrink-0">
            <X className="mr-2 h-4 w-4" />
            Limpiar
          </Button>
        )}

        {/* Espaciador */}
        <div className="flex-1" />

        {/* Búsqueda - a la derecha */}
        <div className="w-full md:w-auto md:min-w-[350px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar en todos los campos..."
              value={filters.search || ''}
              onChange={(e) =>
                onFiltersChange({ ...filters, search: e.target.value, page: 1 })
              }
              className="pl-9"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
